import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getAttributes,
    getAttributeValues,
    previewMergeAttributeValues,
    mergeAttributeValues,
    type MergePayload,
} from "../helpers/Attributes";

type Attribute = { id: number; name: string };
type AttributeValue = {
    id: number;
    value: string;
    product_count?: number;
    created_at?: string;
    updated_at?: string;
};

type PreviewResponse = {
    affected_count: number;
    to_id?: number | null;
    to_value?: string | null;
    will_create_to?: boolean;
    will_delete_old?: boolean;
};

export default function AttributeValuesMergePage() {
    const [loading, setLoading] = useState(false);

    // атрибуты
    const [attributes, setAttributes] = useState<Attribute[]>([]);
    const [attributeId, setAttributeId] = useState<number | null>(null);

    // значения
    const [values, setValues] = useState<AttributeValue[]>([]);
    const [valuesSearch, setValuesSearch] = useState<string>("");

    // выбор "from"
    const [fromMap, setFromMap] = useState<Record<number, boolean>>({});
    const fromIds = useMemo(
        () => Object.entries(fromMap).filter(([, v]) => v).map(([k]) => Number(k)),
        [fromMap]
    );

    // target (to)
    const [toId, setToId] = useState<number | null>(null); // существующее
    const [toValue, setToValue] = useState<string>(""); // создать новое

    const [deleteOld, setDeleteOld] = useState<boolean>(false);
    const [preview, setPreview] = useState<PreviewResponse | null>(null);

    const abortRef = useRef<AbortController | null>(null);
    const searchTimer = useRef<number | undefined>(undefined);

    // --- загрузка списка атрибутов
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                // твой хелпер: getAttributes(limit, page, search, options)
                const resp = await getAttributes(500, 1, null, {});
                const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
                if (mounted) setAttributes(list as Attribute[]);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    // --- загрузка значений текущего атрибута
    const loadValues = useCallback(
        async (aid: number, search: string) => {
            try {
                setLoading(true);
                if (abortRef.current) abortRef.current.abort();
                abortRef.current = new AbortController();

                // твой хелпер: getAttributeValues(attributeId, search, options)
                const resp = await getAttributeValues(aid, search || null, { signal: abortRef.current.signal });
                const list = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
                setValues(list as AttributeValue[]);
            } catch (e: any) {
                if (e?.name === "AbortError") {
                    // ignore
                } else {
                    console.error(e);
                }
            } finally {
                setLoading(false);
            }
        },
        []
    );

    // при смене атрибута — сбросы и загрузка
    useEffect(() => {
        if (attributeId) {
            setFromMap({});
            setToId(null);
            setToValue("");
            setPreview(null);
            loadValues(attributeId, valuesSearch);
        }
    }, [attributeId, loadValues]); // valuesSearch дебаунсим отдельно

    // --- дебаунс поиска по значениям
    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setValuesSearch(val);
        if (searchTimer.current) window.clearTimeout(searchTimer.current);
        searchTimer.current = window.setTimeout(() => {
            if (attributeId) loadValues(attributeId, val);
        }, 300) as unknown as number;
    };

    // --- выбор from
    const toggleFrom = (id: number, checked: boolean) => {
        setFromMap((m) => ({ ...m, [id]: checked }));
        if (checked && toId === id) setToId(null); // цель нельзя выбрать в from
        setPreview(null);
    };

    // --- выбор toId
    const onSelectToId = (id: number | null) => {
        setToId(id);
        if (id) {
            // исключаем выбранную цель из from
            setFromMap((m) => {
                const next = { ...m };
                if (next[id]) delete next[id];
                return next;
            });
        }
        setPreview(null);
    };

    // --- валидация формы
    const canSubmit = useMemo(() => {
        if (!attributeId) return false;
        if (fromIds.length === 0) return false;
        const hasToId = !!toId;
        const hasToValue = toValue.trim().length > 0;
        if (hasToId && hasToValue) return false; // нельзя оба
        if (!hasToId && !hasToValue) return false; // нужен один
        return true;
    }, [attributeId, fromIds.length, toId, toValue]);

    // --- DRY-RUN
    const onDryRun = async () => {
        if (!canSubmit || !attributeId) return;
        try {
            setLoading(true);
            setPreview(null);

            const payload: Omit<MergePayload, "dry_run"> = {
                attribute_id: attributeId,
                from: fromIds,
                to: toId ?? undefined,
                to_value: toId ? undefined : toValue.trim() || undefined,
                delete_old: deleteOld ? true : false,
            };

            const resp = await previewMergeAttributeValues(payload, {});
            setPreview(resp as PreviewResponse);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    // --- MERGE (боевой)
    const onMerge = async () => {
        if (!canSubmit || !attributeId) return;
        if (!window.confirm("Выполнить слияние? Это изменит product_attributes.")) return;
        try {
            setLoading(true);
            const resp = await mergeAttributeValues(
                {
                    attribute_id: attributeId,
                    from: fromIds,
                    to: toId ?? undefined,
                    to_value: toId ? undefined : toValue.trim() || undefined,
                    dry_run: false,
                    delete_old: deleteOld ? true : false,
                },
                {}
            );
            setPreview(resp as PreviewResponse);

            // обновить список значений и сбросить выбор
            await loadValues(attributeId, valuesSearch);
            setFromMap({});
            if ((resp as PreviewResponse)?.to_id) setToId((resp as PreviewResponse).to_id ?? null);
            setToValue("");
            alert("Merge завершён");
        } catch (e) {
            console.error(e);
            alert("Ошибка при merge (см. консоль)");
        } finally {
            setLoading(false);
        }
    };

    // --- сводка
    const summaryText = useMemo(() => {
        const parts: string[] = [];
        parts.push(`attribute_id=${attributeId ?? "—"}`);
        parts.push(`from=[${fromIds.join(", ")}]`);
        if (toId) parts.push(`to_id=${toId}`);
        if (!toId && toValue.trim()) parts.push(`to_value="${toValue.trim()}"`);
        parts.push(`delete_old=${deleteOld ? "YES" : "NO"}`);
        return parts.join(" • ");
    }, [attributeId, fromIds, toId, toValue, deleteOld]);

    const selectedCountByFrom = fromIds.length;
    const totalSelectedProducts = useMemo(() => {
        if (values.length === 0 || fromIds.length === 0) return 0;
        let sum = 0;
        fromIds.forEach((fid) => {
            const v = values.find((x) => x.id === fid);
            sum += v?.product_count ?? 0;
        });
        return sum;
    }, [values, fromIds]);

    return (
        <>
            <div className="flex justify-between items-start gap-4">
                <div className="space-y-2">
                    <h1 className="text-xl font-semibold">Merge attribute values</h1>
                    <div className="text-sm text-gray-600">
                        Повторяет логику команды <code>attributes:merge-values</code>.
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        className="px-3 py-1 rounded border"
                        onClick={() => {
                            setAttributeId(null);
                            setValues([]);
                            setValuesSearch("");
                            setFromMap({});
                            setToId(null);
                            setToValue("");
                            setPreview(null);
                            setDeleteOld(false);
                        }}
                    >
                        Reset
                    </button>
                </div>
            </div>

            <div className="mt-3 p-3 rounded bg-white">
                <div className="flex flex-col gap-4">
                    {/* Шаг 1: выбор атрибута */}
                    <div className="flex flex-wrap items-center gap-2">
                        <label className="text-sm">Attribute</label>
                        <select
                            className="border px-2 py-1 rounded min-w-[240px]"
                            value={attributeId ?? ""}
                            onChange={(e) => setAttributeId(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">— выбери атрибут —</option>
                            {attributes.map((a) => (
                                <option key={a.id} value={a.id}>
                                    {a.name} (#{a.id})
                                </option>
                            ))}
                        </select>

                        <label className="flex items-center gap-2 text-sm ms-4">
                            <input
                                type="checkbox"
                                checked={deleteOld ? true : false}
                                onChange={(e) => setDeleteOld(e.target.checked)}
                            />
                            Delete old values after merge
                        </label>
                    </div>

                    {/* Шаг 2: список значений и выбор from */}
                    {attributeId ? (
                        <div className="flex gap-6">
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="font-medium">Values (select <em>from</em>)</div>
                                    <input
                                        className="border px-2 py-1 rounded w-64"
                                        placeholder="Search values…"
                                        defaultValue={valuesSearch}
                                        onChange={onSearchChange}
                                    />
                                </div>

                                {loading ? (
                                    <div className="p-4 text-gray-600">Loading…</div>
                                ) : values.length === 0 ? (
                                    <div className="p-2 text-gray-500">Нет значений.</div>
                                ) : (
                                    <div className="border rounded">
                                        {values.map((v) => {
                                            const checked = !!fromMap[v.id];
                                            const disabled = toId === v.id;
                                            return (
                                                <div
                                                    key={v.id}
                                                    className="flex items-center justify-between px-2 py-1 border-b last:border-0"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={checked ? true : false}
                                                            disabled={disabled ? true : false}
                                                            onChange={(e) => toggleFrom(v.id, e.target.checked)}
                                                        />
                                                        <div className="text-sm">
                                                            <span className="font-medium">{v.value}</span>{" "}
                                                            <span className="text-gray-500">#{v.id}</span>
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-gray-600">
                                                        {typeof v.product_count === "number"
                                                            ? `${v.product_count} products`
                                                            : "—"}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="mt-2 text-sm text-gray-700">
                                    Выбрано from: <b>{selectedCountByFrom}</b>
                                    {totalSelectedProducts > 0 ? ` • затронет ~${totalSelectedProducts} product_attributes` : ""}
                                </div>
                            </div>

                            {/* Шаг 3: выбор to */}
                            <div className="w-[360px]">
                                <div className="font-medium mb-2">Target (to)</div>

                                <div className="space-y-3">
                                    <div className="border rounded p-2">
                                        <div className="text-sm mb-1">Use existing value</div>
                                        <select
                                            className="border px-2 py-1 rounded w-full"
                                            value={toId ?? ""}
                                            onChange={(e) => onSelectToId(e.target.value ? Number(e.target.value) : null)}
                                        >
                                            <option value="">— none —</option>
                                            {values.map((v) => (
                                                <option key={v.id} value={v.id} disabled={!!fromMap[v.id]}>
                                                    {v.value} (#{v.id})
                                                    {typeof v.product_count === "number" ? ` • ${v.product_count}` : ""}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="border rounded p-2">
                                        <div className="text-sm mb-1">…or create new value</div>
                                        <input
                                            className="border px-2 py-1 rounded w-full"
                                            placeholder='e.g. "4K Ultra HD (3840)"'
                                            value={toValue}
                                            onChange={(e) => {
                                                setToValue(e.target.value);
                                                if (toId) setToId(null);
                                                setPreview(null);
                                            }}
                                        />
                                        <div className="text-xs text-gray-500 mt-1">Если заполнено — toId игнорируется.</div>
                                    </div>
                                </div>

                                <div className="mt-3 space-x-2">
                                    <button
                                        className="px-3 py-1 rounded border disabled:opacity-50"
                                        disabled={loading || !canSubmit}
                                        onClick={onDryRun}
                                    >
                                        Dry-run
                                    </button>
                                    <button
                                        className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
                                        disabled={loading || !canSubmit}
                                        onClick={onMerge}
                                    >
                                        Merge
                                    </button>
                                </div>

                                <div className="mt-3 text-xs text-gray-600">{summaryText}</div>

                                {preview ? (
                                    <div className="mt-3 border rounded p-2 bg-gray-50">
                                        <div className="font-medium mb-1">Preview</div>
                                        <div className="text-sm">
                                            Affected product_attributes: <b>{preview.affected_count}</b>
                                        </div>
                                        <div className="text-sm">
                                            To:{" "}
                                            {preview.to_id
                                                ? <span>#{preview.to_id}</span>
                                                : (preview.to_value ? <span>"{preview.to_value}"</span> : <span>—</span>)}
                                        </div>
                                        <div className="text-sm">
                                            Will create new to: {preview.will_create_to ? "YES" : "NO"}
                                        </div>
                                        <div className="text-sm">
                                            Will delete old: {preview.will_delete_old ? "YES" : "NO"}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : (
                        <div className="text-gray-600">Сначала выбери атрибут.</div>
                    )}
                </div>
            </div>
        </>
    );
}